import { InputCard } from '@/components/input-card';
import { RoundedButton } from '@/components/rounded-button';
import { STALE_TIME_VERY_SHORT } from '@/constants/shared';
import { AssetSelect } from '@/pages/OptimizerVault/AssetSelect';
import { HStack, Text, VStack } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { erc20Abi, formatUnits } from 'viem';
import { useReadContract } from 'wagmi';
import { DepositsAndWithdrawalFrame } from '../DepositAndWithdrawalFrame';
import { useCurrentVault } from '../hooks/useCurrentVault';
import { useWithdrawToken } from './useWithdrawExchangeRate';

export const WithdrawFromVault = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [amount, setAmount] = useState('');
  const [withdrawToken, setWithdrawToken] = useState<{ symbol: string; address: `0x${string}`; decimals: number }>();
  const { chainId, vaultAddress, vaultKey, vault, address, name } = useCurrentVault();
  const { data: vaultTokenBalance, isLoading: isLoadingVaultTokenBalance } = useReadContract({
    abi: erc20Abi,
    // TODO: replace when we get proper obj back from vault response
    // we need to use the selected token address
    address: vaultAddress,
    args: [address as EthAddress],
    functionName: 'balanceOf',
    query: {
      refetchInterval: STALE_TIME_VERY_SHORT,
    },
  });
  const goToSteps = () => {
    navigate(`${pathname}/steps?to_amount=${amount}`);
  };

  const formattedVaultTokenBalance = useMemo(() => {
    if (!vaultTokenBalance || !vault?.token.decimals) return 0;
    return Number(formatUnits(vaultTokenBalance, vault.token.decimals));
  }, [vault?.token.decimals, vaultTokenBalance]);

  const withdrawAssets = useMemo(() => {
    if (!chainId) return [];
    return Object.values(vault?.withdraw?.sourceChains[chainId]?.destinationChains[chainId]?.wantTokens ?? []);
  }, [chainId, vault?.withdraw?.sourceChains]);

  useEffect(() => {
    setWithdrawToken(withdrawAssets[0]);
  }, [withdrawAssets, setWithdrawToken]);

  const { exchangeRate } = useWithdrawToken();

  const isDisabled = Number(amount) <= 0 || Number(amount) > formattedVaultTokenBalance;
  return (
    <DepositsAndWithdrawalFrame title={`Withdraw from ${name}`}>
      <VStack gap={6} w={520} m="auto">
        <>
          <InputCard
            header="Withdraw To"
            balance={formattedVaultTokenBalance}
            value={amount}
            loading={isLoadingVaultTokenBalance}
            onChange={setAmount}
            denom={vaultKey}
            endElement={
              <AssetSelect
                selectedToken={withdrawToken}
                setSelectedToken={setWithdrawToken}
                tokenOptions={withdrawAssets}
              />
            }
          />

          <RoundedButton onClick={goToSteps} w="full" disabled={isDisabled}>
            Withdraw
          </RoundedButton>
          <WithdrawDetailRow title="Exchange Rate" value={exchangeRate.toString()} />
          <WithdrawDetailRow title="Estimated Processing Time" value="<1 minute" />
          <WithdrawDetailRow title="Network Fee" value="$0.001" />
        </>
      </VStack>
    </DepositsAndWithdrawalFrame>
  );
};

const WithdrawDetailRow = ({ title, value }: { title: string; value: string }) => (
  <HStack justifyContent="space-between" w="full">
    <Text fontSize="sm" color="fg.muted" fontWeight="400">
      {title}
    </Text>
    <Text fontSize="sm" color="fg" fontWeight="400">
      {value}
    </Text>
  </HStack>
);
